import React from 'react';
import { Send, Info, AlertTriangle, ShieldCheck, Sparkles, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CAMPAIGN_CREDITS, CAMPAIGN_INFO } from '../mock';
import { Button } from './ui/button';
import { waLink } from '../lib/contact';

const iconByType = {
  info: { Icon: Info, cls: 'text-slate-500 bg-slate-100' },
  warning: { Icon: AlertTriangle, cls: 'text-amber-600 bg-amber-50 border-amber-100' },
  success: { Icon: ShieldCheck, cls: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
};

const CreditCard = ({ c }) => (
  <div
    className={`relative rounded-2xl p-6 border transition-all hover:-translate-y-0.5 hover:shadow-xl ${
      c.popular
        ? 'bg-[#0a0e27] text-white border-[#0a0e27] shadow-lg'
        : 'bg-white border-slate-200'
    }`}
  >
    {c.popular && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#FF5B24] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
        {c.badge}
      </span>
    )}

    <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${c.popular ? 'text-[#FF5B24]' : 'text-[#FF5B24]'}`}>
      <Send size={11} /> Pacote
    </div>

    <div className="mt-3 flex items-baseline gap-1.5">
      <span className={`text-4xl font-black leading-none ${c.popular ? 'text-white' : 'text-[#0a0e27]'}`}>
        {c.credits}
      </span>
      <span className={`text-sm font-semibold ${c.popular ? 'text-white/70' : 'text-slate-500'}`}>
        mensagens
      </span>
    </div>

    <div className={`mt-6 border-t pt-4 ${c.popular ? 'border-white/10' : 'border-slate-100'}`}>
      <div className="flex items-baseline gap-1">
        <span className={`text-sm font-semibold ${c.popular ? 'text-white/70' : 'text-slate-400'}`}>R$</span>
        <span className={`text-3xl font-black ${c.popular ? 'text-white' : 'text-[#0a0e27]'}`}>{c.price}</span>
      </div>
      <div className={`text-xs mt-1 ${c.popular ? 'text-white/60' : 'text-slate-500'}`}>
        Equivale a R$ {c.costPerMessage} por mensagem
      </div>
    </div>

    <Button
      asChild
      className={`mt-6 w-full h-10 rounded-full text-sm font-semibold ${
        c.popular ? 'btn-coral' : 'bg-[#0a0e27] text-white hover:bg-[#1a1f4d]'
      }`}
    >
      <a
        href={waLink(`Olá! Quero adquirir o pacote de ${c.credits} mensagens (R$ ${c.price}) para campanhas em massa.`)}
        target="_blank"
        rel="noopener noreferrer"
      >
        Adquirir créditos <ArrowUpRight size={14} className="ml-1" />
      </a>
    </Button>
  </div>
);

const CampaignCredits = () => {
  const navigate = useNavigate();
  return (
    <section id="campanhas" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">
            <Sparkles size={13} /> Add-on
          </div>
          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#0a0e27] leading-tight">
            Campanhas de <span className="text-[#FF5B24]">Mensagens em Massa</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Envie a mesma mensagem para centenas ou milhares de contatos em poucos segundos.
          </p>
          <p className="mt-2 text-slate-500 text-sm max-w-2xl mx-auto">
            Adquira créditos de campanha e potencialize sua comunicação com clientes de forma rápida e prática.
            Os créditos são vendidos separadamente e podem ser utilizados por clientes com um plano ativo.
          </p>
        </div>

        {/* Credit packages */}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CAMPAIGN_CREDITS.map((c) => (
            <CreditCard key={c.credits} c={c} />
          ))}
        </div>

        {/* Important info */}
        <div className="mt-14 max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-[#FFF1EA] flex items-center justify-center">
              <Info size={16} className="text-[#FF5B24]" />
            </div>
            <h3 className="text-xl font-extrabold text-[#0a0e27]">Informações importantes</h3>
          </div>

          <div className="space-y-3">
            {CAMPAIGN_INFO.map((item, i) => {
              const { Icon, cls } = iconByType[item.type];
              return (
                <div
                  key={i}
                  className={`flex items-start gap-3 rounded-xl border p-4 ${
                    item.type === 'info' ? 'bg-slate-50 border-slate-200' : cls
                  }`}
                >
                  <span
                    className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                      item.type === 'info' ? 'bg-white text-slate-500' : cls
                    }`}
                  >
                    <Icon size={15} />
                  </span>
                  <p className="text-sm text-slate-700 leading-relaxed pt-1">{item.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <Button
              onClick={() => navigate('/contato')}
              className="btn-coral rounded-full h-12 px-6 text-sm font-semibold gap-1.5"
            >
              Falar com nosso time sobre campanhas <ArrowUpRight size={15} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CampaignCredits;
